import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

/**
 * Diálogo de confirmación para borrar un borrador.
 *
 * Requiere que el usuario escriba EXACTAMENTE el nombre del empleado
 * para habilitar el botón de borrar (doble confirmación).
 *
 * @param {boolean}  open
 * @param {function} onOpenChange
 * @param {object|null} draft   Borrador a borrar: { id, nombre, ... }
 * @param {function} onConfirm  (draftId) => Promise — handler que ejecuta el borrado
 * @param {boolean}  deleting   true mientras se está borrando
 */
export function DeleteDraftDialog({ open, onOpenChange, draft, onConfirm, deleting }) {
  const [typedName, setTypedName] = useState('');

  const expectedName = (draft?.nombre || '').trim();
  const nameMatches = typedName.trim() === expectedName && expectedName.length > 0;

  // Reset al abrir/cerrar el dialog o cambiar de draft
  useEffect(() => {
    if (!open) {
      setTypedName('');
    }
  }, [open, draft?.id]);

  const handleConfirm = () => {
    if (!nameMatches || !draft?.id || deleting) return;
    onConfirm(draft.id);
  };

  const handleCancel = () => {
    if (deleting) return;
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !deleting && onOpenChange(v)}>
      <DialogContent style={{ maxWidth: '520px' }} data-testid="delete-draft-dialog">
        <DialogHeader>
          <DialogTitle style={{ color: '#b91c1c', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🗑️ Borrar borrador
          </DialogTitle>
          <DialogDescription>
            Esta acción es <strong>irreversible</strong>. El borrador y todos sus datos se eliminarán permanentemente.
          </DialogDescription>
        </DialogHeader>

        <div style={{ padding: '16px 0' }}>
          {/* Aviso */}
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderLeft: '4px solid #dc2626',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#991b1b',
            lineHeight: 1.6
          }}>
            Vas a borrar el borrador del empleado:
            <div
              data-testid="delete-draft-expected-name"
              style={{ fontWeight: 700, color: '#7f1d1d', fontSize: '15px', margin: '6px 0 2px' }}
            >
              {expectedName || '(sin nombre)'}
            </div>
            <span style={{ fontSize: '12px', color: '#b91c1c' }}>
              ID: <code style={{ fontFamily: 'Source Code Pro, monospace' }}>{draft?.id}</code>
            </span>
          </div>

          {/* Input de confirmación */}
          <Label htmlFor="delete-confirm-name" style={{ fontSize: '13px', fontWeight: 600 }}>
            Para confirmar, escribe el nombre del empleado exactamente como aparece arriba:
          </Label>
          <Input
            id="delete-confirm-name"
            data-testid="delete-draft-name-input"
            placeholder={expectedName}
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            disabled={deleting}
            style={{
              marginTop: '8px',
              borderColor: typedName && !nameMatches ? '#dc2626' : undefined
            }}
            autoFocus
          />
          {typedName && !nameMatches && (
            <p
              data-testid="delete-draft-name-mismatch"
              style={{ color: '#dc2626', fontSize: '11.5px', marginTop: '6px' }}
            >
              El nombre no coincide.
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button
            type="button"
            variant="outline"
            data-testid="delete-draft-cancel-button"
            onClick={handleCancel}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            data-testid="delete-draft-confirm-button"
            onClick={handleConfirm}
            disabled={!nameMatches || deleting}
            style={{
              background: nameMatches && !deleting ? '#dc2626' : '#9ca3af',
              color: '#fff'
            }}
          >
            {deleting ? '⏳ Borrando...' : '🗑️ Borrar definitivamente'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteDraftDialog;
